/**
 * Product cards: keep title + price on one row when they fit;
 * stack price under the title when the card is too narrow.
 * Used on PLP, search page, blog guide products, and search modal.
 */
(function () {
  const ROOT_SELECTORS = [
    '.plp-collection-section',
    '.plp-search-section',
    '.blog-guide-products-list',
    '.ub-search-modal',
    'new-search',
  ];
  const ROW_SELECTOR = '.new-card-info';
  const STACKED_CLASS = 'new-card-info--stacked';

  let resizeTimer;
  let mutationTimer;

  function getRoots(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    const roots = [];

    ROOT_SELECTORS.forEach((selector) => {
      if (root.matches?.(selector)) {
        roots.push(root);
      }
      root.querySelectorAll?.(selector).forEach((el) => {
        if (!roots.includes(el)) roots.push(el);
      });
    });

    if (root === document || !scope) {
      ROOT_SELECTORS.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          if (!roots.includes(el)) roots.push(el);
        });
      });
    }

    return roots;
  }

  function getAvailableWidth(row) {
    // Prefer a width-constrained ancestor so nowrap measure can't expand the row
    // (common on placeholder / card--text cards with no product image).
    const candidates = [
      row.closest('.card__information'),
      row.closest('.card__content'),
      row.closest('.product-card-wrapper'),
      row.closest('.card-wrapper'),
      row.closest('.grid__item'),
      row,
    ].filter(Boolean);

    const widths = candidates
      .map((el) => el.clientWidth || el.getBoundingClientRect().width || 0)
      .filter((w) => w > 0);

    if (!widths.length) return 0;

    // Tightest real constraint wins
    return Math.min(...widths);
  }

  function restoreRowMeasureState(row, title, price, hadStacked, state) {
    title.style.cssText = state.prevTitleCss;
    price.style.cssText = state.prevPriceCss;
    row.style.cssText = state.prevRowCss;
    if (hadStacked) row.classList.add(STACKED_CLASS);
    else row.classList.remove(STACKED_CLASS);
  }

  /**
   * Measure whether title + price fit on a single row.
   * Locks the row to the card's real width first so nowrap measurement
   * cannot expand placeholder / text-only cards and falsely report a fit.
   */
  function fitsOnOneLine(row) {
    const title = row.querySelector(':scope > a');
    const price = row.querySelector(':scope > .price');
    if (!title || !price) return true;

    const hadStacked = row.classList.contains(STACKED_CLASS);
    const state = {
      prevTitleCss: title.getAttribute('style') || '',
      prevPriceCss: price.getAttribute('style') || '',
      prevRowCss: row.getAttribute('style') || '',
    };

    const available = getAvailableWidth(row);
    if (!available) {
      return null;
    }

    row.classList.remove(STACKED_CLASS);
    row.style.setProperty('display', 'flex', 'important');
    row.style.setProperty('flex-direction', 'row', 'important');
    row.style.setProperty('flex-wrap', 'nowrap', 'important');
    row.style.setProperty('width', `${available}px`, 'important');
    row.style.setProperty('max-width', `${available}px`, 'important');
    row.style.setProperty('overflow', 'hidden', 'important');

    title.style.setProperty('display', 'block', 'important');
    title.style.setProperty('white-space', 'nowrap', 'important');
    title.style.setProperty('overflow', 'visible', 'important');
    title.style.setProperty('text-overflow', 'clip', 'important');
    title.style.setProperty('-webkit-line-clamp', 'unset', 'important');
    title.style.setProperty('-webkit-box-orient', 'unset', 'important');
    title.style.setProperty('width', 'auto', 'important');
    title.style.setProperty('max-width', 'none', 'important');
    title.style.setProperty('flex', '0 0 auto', 'important');

    price.style.setProperty('display', 'block', 'important');
    price.style.setProperty('white-space', 'nowrap', 'important');
    price.style.setProperty('flex', '0 0 auto', 'important');
    price.style.setProperty('width', 'auto', 'important');

    void row.offsetWidth;

    const styles = getComputedStyle(row);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const needed = title.scrollWidth + price.scrollWidth + gap;
    const fits = needed <= available + 1;

    restoreRowMeasureState(row, title, price, hadStacked, state);

    return fits;
  }

  function updateCardLayouts(scope, attempt = 0) {
    const roots = getRoots(scope);
    if (!roots.length) return;

    let needsRetry = false;

    roots.forEach((root) => {
      root.querySelectorAll(ROW_SELECTOR).forEach((row) => {
        // Cards with the Members Only badge keep title + price on one row
        if (row.hasAttribute('data-member-badge')) {
          row.classList.remove(STACKED_CLASS);
          return;
        }
        const fits = fitsOnOneLine(row);
        if (fits === null) {
          // Prefer stacked until we can measure — avoids price sticking beside long titles
          row.classList.add(STACKED_CLASS);
          needsRetry = true;
          return;
        }
        row.classList.toggle(STACKED_CLASS, !fits);
      });
    });

    if (needsRetry && attempt < 6) {
      requestAnimationFrame(() => updateCardLayouts(scope, attempt + 1));
    } else if (needsRetry && attempt === 6) {
      setTimeout(() => updateCardLayouts(scope, attempt + 1), 150);
    }
  }

  function scheduleUpdate(scope, delay = 0) {
    clearTimeout(resizeTimer);

    const run = () => {
      updateCardLayouts(scope || document);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => updateCardLayouts(scope || document));
      });
    };

    if (delay) {
      resizeTimer = setTimeout(run, delay);
    } else {
      run();
    }
  }

  function observeRoot(el) {
    if (!el || el.dataset.cardLayoutObserved === 'true') return;
    el.dataset.cardLayoutObserved = 'true';

    const observer = new MutationObserver(() => {
      clearTimeout(mutationTimer);
      mutationTimer = setTimeout(() => scheduleUpdate(el), 50);
    });
    observer.observe(el, { childList: true, subtree: true });
  }

  function init() {
    updateCardLayouts(document);
    getRoots(document).forEach(observeRoot);

    const productGrid = document.getElementById('product-grid');
    if (productGrid) observeRoot(productGrid);

    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) observeRoot(gridContainer);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => scheduleUpdate(document));
    }

    window.addEventListener('resize', () => scheduleUpdate(document, 100));
    window.addEventListener('load', () => scheduleUpdate(document, 50));

    document.addEventListener('shopify:section:load', (event) => {
      updateCardLayouts(event.target);
      getRoots(event.target).forEach(observeRoot);
    });
  }

  window.PlpCardLayout = {
    update: (scope) => updateCardLayouts(scope || document),
    scheduleUpdate,
    observe: observeRoot,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
